import {
  queryOptions,
  useMutation,
  UseMutationOptions,
  useQueryClient
} from '@tanstack/react-query';
import { useApiClient } from 'bff/ApiClientProvider';
import {
  PaymentDetailsDeleteBankAccountRequest,
  PaymentDetailsGetJurisdictionSchemesRequest,
  PaymentDetailsGetRequest,
  PaymentDetailsPostRequest,
  PaymentDetailsUpsertBankAccountRequest,
  ResponseError,
  Result,
  ResultOfBankAccountViewModel,
  ResultStates
} from 'bff/client';
import { useInvalidateProposalInformation } from 'bff/hooks/common/ProposalsInfosApi';
import { UseDisplayErrorMutationOptions } from 'bff/types/display-error-props.type';
import { secondsToMilliseconds } from 'date-fns';
import { clientService } from 'stores/client-store/client-store';

export const paymentDetailsKeys = {
  base: () => ['payment-details'] as const,
  fetch: (request: PaymentDetailsGetRequest) => [...paymentDetailsKeys.base(), request] as const,
  submit: () => [...paymentDetailsKeys.base(), 'submit'] as const,
  bankAccount: () => [...paymentDetailsKeys.base(), 'upsert-bank-account'] as const,
  jurisdictionSchemes: (request: PaymentDetailsGetJurisdictionSchemesRequest) =>
    [...paymentDetailsKeys.base(), 'get-jurisdiction-schemes', request] as const
};

export const useInvalidatePaymentDetails = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: paymentDetailsKeys.base(),
      refetchType: 'all'
    });
};

export const getPaymentDetailsOptions = (
  getPersonalDetailsViewRequest: PaymentDetailsGetRequest
) => {
  const { paymentDetailsApi } = clientService.getApiClient();
  return queryOptions({
    staleTime: secondsToMilliseconds(30),
    queryKey: paymentDetailsKeys.fetch(getPersonalDetailsViewRequest),
    queryFn: () => paymentDetailsApi.paymentDetailsGet(getPersonalDetailsViewRequest),
    select: (response) => response.data!
  });
};

export const useSavePaymentDetails = (
  options?: UseDisplayErrorMutationOptions<Result, PaymentDetailsPostRequest>
) => {
  const { paymentDetailsApi } = clientService.getApiClient();
  const invalidatePaymentDetails = useInvalidatePaymentDetails();
  const invalidateProposalInformation = useInvalidateProposalInformation();

  return useMutation({
    mutationKey: paymentDetailsKeys.submit(),
    mutationFn: (request: PaymentDetailsPostRequest) =>
      paymentDetailsApi.paymentDetailsPost(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        await Promise.allSettled([invalidatePaymentDetails(), invalidateProposalInformation()]);
      }
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

export const useUpsertBankAccount = (
  options?: UseDisplayErrorMutationOptions<
    ResultOfBankAccountViewModel,
    PaymentDetailsUpsertBankAccountRequest
  >
) => {
  const { paymentDetailsApi } = useApiClient();
  const invalidatePaymentDetails = useInvalidatePaymentDetails();

  return useMutation({
    mutationKey: paymentDetailsKeys.bankAccount(),
    mutationFn: (request: PaymentDetailsUpsertBankAccountRequest) =>
      paymentDetailsApi.paymentDetailsUpsertBankAccount(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        options?.onSuccess?.(data, variables, ctx);

        await Promise.allSettled([invalidatePaymentDetails()]);
      }
    }
  });
};

export const useDeleteBankAccount = (
  options?: UseMutationOptions<Result, ResponseError, PaymentDetailsDeleteBankAccountRequest>
) => {
  const { paymentDetailsApi } = useApiClient();
  const invalidatePaymentDetails = useInvalidatePaymentDetails();
  const invalidateProposalInformation = useInvalidateProposalInformation();

  const mutationOptions: typeof options = {
    ...options,
    mutationKey: ['investment-details', 'useDeleteProposal'],
    mutationFn: async (deleteDeleteBankAccountRequest: PaymentDetailsDeleteBankAccountRequest) =>
      await paymentDetailsApi.paymentDetailsDeleteBankAccount(deleteDeleteBankAccountRequest),
    onSuccess: async (...args) => {
      await invalidateProposalInformation();
      await invalidatePaymentDetails();

      if (options?.onSuccess) {
        options.onSuccess(...args);
      }
    }
  };

  return useMutation(mutationOptions);
};

export const getPaymentDetailsGetJurisdictionSchemesRequest = (
  getPaymentDetailsGetJurisdictionSchemesRequest: PaymentDetailsGetJurisdictionSchemesRequest
) => {
  const { paymentDetailsApi } = clientService.getApiClient();
  return queryOptions({
    staleTime: secondsToMilliseconds(30),
    queryKey: paymentDetailsKeys.jurisdictionSchemes(
      getPaymentDetailsGetJurisdictionSchemesRequest
    ),
    queryFn: () =>
      paymentDetailsApi.paymentDetailsGetJurisdictionSchemes(
        getPaymentDetailsGetJurisdictionSchemesRequest
      ),
    select: (response) => response.data!
  });
};
